import type { UseQueryResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { getSolarProposalErrorStatus } from "./useSolarProposal"
import type { SolarHdmDto } from "../dto/solar-proposal-hdm.dto"
import { solarProposalService } from "../services/solar-proposal.service"

const ACCESS_FAILURE_STATUSES = new Set([401, 403, 404])

/** An access failure is an answer; anything else retries, twice. */
export const retrySolarHdmRead = (
  failureCount: number,
  error: unknown,
): boolean =>
  !ACCESS_FAILURE_STATUSES.has(getSolarProposalErrorStatus(error) ?? 0) &&
  failureCount < 2

/**
 * Contract › HDM: the quotes already created and the quote Vision proposes.
 * The read calls HDM, so it is read on every open but not on focus; HDM being down comes
 * back as `unavailableReason`, not an error.
 */
export default function useSolarProposalHdm(
  proposalId: string | undefined,
): UseQueryResult<SolarHdmDto, unknown> {
  return useQuery({
    queryKey: queryKeys.solarProposal.hdm(proposalId ?? ``),
    queryFn: () => solarProposalService.getHdm(proposalId ?? ``),
    enabled: Boolean(proposalId),
    // The draft reflects the proposal as it is now: equipment, utility or
    // pricing saved elsewhere must show the next time the drawer opens.
    staleTime: 0,
    refetchOnMount: `always`,
    refetchOnWindowFocus: false,
    retry: retrySolarHdmRead,
  })
}
